/**
 * CashFlowChart Component
 * Line chart visualization for budget cash flow analysis
 * Shows planned vs actual vs forecast spending over time
 */

import React, { useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { TrendingUp, Calendar, DollarSign, RefreshCw } from 'lucide-react';
import type { CashFlowDataPoint } from '../../types/budget';
import { formatCurrency as formatCurrencyUtil } from '../../utils/budgetCalculations';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface CashFlowChartProps {
  data: CashFlowDataPoint[];
  currency?: string;
  showForecast?: boolean;
  height?: number;
  onRefresh?: () => void;
  isLoading?: boolean;
  className?: string;
}

const CashFlowChart: React.FC<CashFlowChartProps> = ({
  data,
  currency = 'USD',
  showForecast = true,
  height = 300,
  onRefresh,
  isLoading = false,
  className = '',
}) => {
  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalPlanned: 0,
        totalActual: 0,
        variance: 0,
        variancePercent: 0,
        latestCumulative: 0,
      };
    }

    const totalPlanned = data.reduce((sum, d) => sum + (d.planned || 0), 0);
    const totalActual = data.reduce((sum, d) => sum + (d.actual || 0), 0);
    const variance = totalPlanned - totalActual;
    const variancePercent = totalPlanned > 0 ? (variance / totalPlanned) * 100 : 0;
    const latestCumulative = data[data.length - 1]?.cumulative || 0;

    return {
      totalPlanned,
      totalActual,
      variance,
      variancePercent,
      latestCumulative,
    };
  }, [data]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const labels = data.map(d => d.date);

    const datasets = [
      {
        label: 'Planned',
        data: data.map(d => d.planned || 0),
        borderColor: '#3B82F6', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: false,
        tension: 0.3,
      },
      {
        label: 'Actual',
        data: data.map(d => d.actual || 0),
        borderColor: '#10B981', // green-500
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        tension: 0.3,
      },
      {
        label: 'Cumulative',
        data: data.map(d => d.cumulative || 0),
        borderColor: '#8B5CF6', // purple-500
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        fill: false,
        tension: 0.3,
        yAxisID: 'y1',
      },
    ];

    // Remove cumulative if not showing forecast
    if (!showForecast) {
      datasets.pop();
    }

    return { labels, datasets };
  }, [data, showForecast]);

  // Chart options
  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#1F2937',
          bodyColor: '#4B5563',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          usePointStyle: true,
          callbacks: {
            label: function (context) {
              const value = context.raw as number;
              return `${context.dataset.label}: ${formatCurrencyUtil(value, currency)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 11,
            },
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            font: {
              size: 11,
            },
            callback: function (value) {
              return formatCurrencyUtil(Number(value), currency);
            },
          },
        },
        ...(showForecast && {
          y1: {
            type: 'linear' as const,
            display: true,
            position: 'right' as const,
            beginAtZero: true,
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              font: {
                size: 11,
              },
              callback: function (value) {
                return formatCurrencyUtil(Number(value), currency);
              },
            },
          },
        }),
      },
    }),
    [currency, showForecast]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  // Format currency helper
  const formatAmount = (amount: number) => formatCurrencyUtil(amount, currency);

  if (!data || data.length === 0) {
    return (
      <div className={`bg-white border border-gray-200 rounded-xl p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-brand-600" />
            <h3 className="text-lg font-semibold text-gray-900">Cash Flow Analysis</h3>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <Calendar className="h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-sm">No cash flow data available</p>
          <p className="text-gray-400 text-xs mt-1">
            Cash flow data will appear once budget spending begins
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <TrendingUp className="h-5 w-5 text-brand-600" />
          <h3 className="text-lg font-semibold text-gray-900">Cash Flow Analysis</h3>
        </div>
        {onRefresh && (
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-2 text-blue-600 mb-1">
            <DollarSign className="h-4 w-4" />
            <span className="text-xs font-medium">Planned</span>
          </div>
          <span className="text-lg font-bold text-blue-700">
            {formatAmount(summaryStats.totalPlanned)}
          </span>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-center space-x-2 text-green-600 mb-1">
            <DollarSign className="h-4 w-4" />
            <span className="text-xs font-medium">Actual</span>
          </div>
          <span className="text-lg font-bold text-green-700">
            {formatAmount(summaryStats.totalActual)}
          </span>
        </div>
        <div
          className={`p-3 rounded-lg ${summaryStats.variance >= 0 ? 'bg-green-50' : 'bg-red-50'}`}
        >
          <div
            className={`flex items-center space-x-2 mb-1 ${summaryStats.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            <TrendingUp className="h-4 w-4" />
            <span className="text-xs font-medium">Variance</span>
          </div>
          <span
            className={`text-lg font-bold ${summaryStats.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}
          >
            {summaryStats.variance >= 0 ? '+' : ''}
            {formatAmount(summaryStats.variance)}
          </span>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="flex items-center space-x-2 text-purple-600 mb-1">
            <Calendar className="h-4 w-4" />
            <span className="text-xs font-medium">YTD Spent</span>
          </div>
          <span className="text-lg font-bold text-purple-700">
            {formatAmount(summaryStats.latestCumulative)}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height }}>
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-pulse text-gray-400">Loading chart data...</div>
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>

      {/* Legend Explanation */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-0.5 border-b-2 border-dashed border-blue-500" />
            <span>Planned spending based on budget allocation</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-0.5 bg-green-500" />
            <span>Actual recorded expenditure</span>
          </div>
          {showForecast && (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-0.5 bg-purple-500" />
              <span>Cumulative total (right axis)</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashFlowChart;
